import * as _ from "lodash";
import * as echarts from "echarts";
import { exportToCSV } from "../../common/utils/exportToCSV";
import { GraphColourArray } from "../../common/enums/GraphColours";

class SecurityBalancesComponent implements angular.IOnInit, angular.IOnChanges {
  public securityBalances;
  public securityBalancesGridOptions;
  public totalAssetClassMarketValue;
  public totalMarketValue;

  public sectorBalances;
  public geographyBalances;
  public currencyBalances;
  // sector chart
  public sectorPieChart;
  public sectorPieChartOptions;

  // geography chart
  public geographyPieChart;
  public geographyPieChartOptions;

  // currency chart
  public currencyPieChart;
  public currencyPieChartOptions;

  // security bar chart
  public securitiesBarChart;
  public securitiesBarChartOptions;

  private minScreenWidth: number = 400;

  public displayAllSecurities: boolean = true;

  public displayCurrency;

  public topSecuritiesLimit = 10;

  public sectorNames;
  public geographyNames;
  public confidentBlue = "#00475a";

  /** @ngInject */
  constructor(
    private $scope: angular.IScope,
    private $window: angular.IWindowService,
    private $state: angular.ui.IStateService
  ) {
    this.sectorPieChart = echarts.init(
      document.getElementById("sector-pie-chart")
    );
    this.geographyPieChart = echarts.init(
      document.getElementById("geography-pie-chart")
    );
    this.currencyPieChart = echarts.init(
      document.getElementById("currency-pie-chart")
    );
    this.securitiesBarChart = echarts.init(
      document.getElementById("security-bar-chart")
    );

    this.$scope.$watch(
      () =>
        this.sectorPieChart.getDom().parentElement.parentElement.clientWidth,
      this.sectorPieChart.resize()
    );
    this.$scope.$watch(
      () =>
        this.geographyPieChart.getDom().parentElement.parentElement
          .clientHeight,
      this.geographyPieChart.resize()
    );
    this.$scope.$watch(
      () =>
        this.currencyPieChart.getDom().parentElement.parentElement.clientHeight,
      this.currencyPieChart.resize()
    );
    this.$scope.$watch(
      () =>
        this.securitiesBarChart.getDom().parentElement.parentElement
          .clientHeight,
      this.securitiesBarChart.resize()
    );
  }

  $onInit = () => {
    this.securityBalancesGridOptions = this.$scope[
      "securityBalancesGridOptions"
    ] = {
      data: [],
      urlSync: false,
    };

    if (this.securityBalances && this.securityBalances.length > 0) {
      this.securityBalancesGridOptions
        ? (this.securityBalancesGridOptions.data = this.securityBalances)
        : null;
      this.getSectorBalances();
      this.getGeographyBalances();
      this.getCurrencyBalances();
      this.getSecurityBarChart();
      this.getSectorNames();
      this.getGeographyNames();
    }
  };

  $onChanges = (onChangesObj: angular.IOnChangesObject) => {
    if (onChangesObj.securityBalances || onChangesObj.assetClassName) {
      this.securityBalancesGridOptions
        ? (this.securityBalancesGridOptions.data = this.securityBalances)
        : null;
      this.getSectorBalances();
      this.getGeographyBalances();
      this.getCurrencyBalances();
      this.getSecurityBarChart();
      this.getSectorNames();
      this.getGeographyNames();
    }
  };

  getSectorNames = () => {
    if (this.securityBalances && this.securityBalances.length > 0) {
      this.sectorNames = _.uniq(_.map(this.securityBalances, "sectorName"));
    }
  };

  getGeographyNames = () => {
    if (this.securityBalances && this.securityBalances.length > 0) {
      this.geographyNames = _.uniq(
        _.map(this.securityBalances, "geographyName")
      );
    }
  };

  getPercentageOfAssetClass = (marketValue) => {
    return ((marketValue * 100) / this.totalAssetClassMarketValue).toFixed(2);
  };

  getPercentageOfTotalMarketValue = (marketValue) => {
    return ((marketValue * 100) / this.totalMarketValue).toFixed(2);
  };

  getDeltaPercentage = (balance) => {
    return (
      +this.getPercentageOfAssetClass(balance.totalMarketValue) -
      balance.modelPercentage
    );
  };

  getSectorBalances = () => {
    let count = -1;
    this.sectorBalances = _.chain(this.securityBalances)
      .groupBy("sectorName")
      .map((balances, sector) => {
        count += 1;
        return {
          name: sector == "null" ? "Unknown" : sector,
          value: balances.reduce((prev, next) => {
            return prev + next.totalMarketValue;
          }, 0),
          itemStyle: {
            normal: {
              color: GraphColourArray[count],
              fontFamily: "Helvetica",
            },
          },
        };
      })
      .value();

    let that = this;
    this.sectorPieChartOptions = this.getDefaultChartOptions(
      "Sectors",
      this.sectorBalances
    );
    if (this.sectorPieChart) {
      this.sectorPieChartOptions.series.data =
        this.sectorPieChartOptions.series.data.filter((item) => item.value > 0);
      this.sectorPieChart.setOption(this.sectorPieChartOptions);
    } else {
      console.log("sector chart not loaded", this.sectorPieChart);
    }
  };

  getGeographyBalances = () => {
    let that = this;
    let count = -1;
    this.geographyBalances = _.chain(this.securityBalances)
      .groupBy("geographyName")
      .map((balances, sector) => {
        count += 1;
        return {
          name: sector == "null" ? "Unknown" : sector,
          value: balances.reduce((prev, next) => {
            return prev + next.totalMarketValue;
          }, 0),
          itemStyle: {
            normal: {
              color: GraphColourArray[count],
              fontFamily: "Helvetica",
            },
          },
        };
      })
      .value();

    this.geographyPieChartOptions = this.getDefaultChartOptions(
      "Geographies",
      this.geographyBalances
    );

    if (this.geographyPieChart) {
      this.geographyPieChartOptions.series.data =
        this.geographyPieChartOptions.series.data.filter(
          (item) => item.value > 0
        );
      this.geographyPieChart.setOption(this.geographyPieChartOptions);
    } else {
      console.log("geography chart not loaded", this.geographyPieChart);
    }
  };

  getCurrencyBalances = () => {
    let that = this;
    let count = -1;
    this.currencyBalances = _.chain(this.securityBalances)
      .groupBy("currency")
      .map((balances, sector) => {
        count += 1;
        return {
          name: sector == "null" ? "Unknown" : sector,
          value: balances.reduce((prev, next) => {
            return prev + next.totalMarketValue;
          }, 0),
          itemStyle: {
            normal: {
              color: GraphColourArray[count],
              fontFamily: "Helvetica",
            },
          },
        };
      })
      .value();
    this.currencyPieChartOptions = this.getDefaultChartOptions(
      "Currencies",
      this.currencyBalances
    );

    if (this.currencyPieChart) {
      this.currencyPieChartOptions.series.data =
        this.currencyPieChartOptions.series.data.filter(
          (item) => item.value > 0
        );
      this.currencyPieChart.setOption(this.currencyPieChartOptions);
    } else {
      console.log("currency chart not loaded", this.currencyPieChart);
    }
  };

  getSecurityBalancesForClients = (security) => {
    var url = this.$state.href("root.portfolioManagement.clientBalances", {
      securityId: security.id,
    });
    this.$window.open(url, "_blank");
  };

  getSecurityBarChart = () => {
    let that = this;
    let securityBalances = [];
    if (this.securityBalances) {
      securityBalances = this.securityBalances
        .slice(0, this.topSecuritiesLimit)
        .filter((balance) => balance.totalMarketValue > 0);
    }

    this.securitiesBarChartOptions = {
      title: {
        text: `\t\t Top ${securityBalances.length} Securities`,
        textStyle: {
          fontFamily: "Seravek",
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "3%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          dataView: {
            readOnly: true,
            title: "View Data",
          },
          saveAsImage: {
            title: "Save as Image",
          },
          magicType: {
            type: ["line", "bar"],
            title: {
              bar: "Bar Chart",
              line: "Line Chart",
            },
          },
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: function (params) {
          return ` ${params[0].name} -  ${params[0].data.toLocaleString(
            "en-EN",
            { style: "currency", currency: that.displayCurrency }
          )}
          - ${that.getPercentageOfAssetClass(params[0].data)} %`;
        },
      },
      xAxis: {
        type: "value",
        name: `\n \n Market Value`,
        nameLocation: "start",
        nameTextStyle: {
          fontWeight: "bold",
        },
        splitInterval: 1,
        axisLabel: {
          formatter: (params) => {
            return params > 0
              ? `${this.displayCurrency} ${this.normaliseNumbers(params)}`
              : "";
          },
        },
      },
      yAxis: {
        type: "category",
        data: securityBalances.map((balance) => balance.description).reverse(),
        axisLabel: {
          formatter: (params) => {
            return params.substr(0, 25);
          },
        },
      },
      series: [
        {
          type: "bar",
          data: securityBalances
            .map((balance) => {
              return {
                value: balance.totalMarketValue,
                itemStyle: { color: this.confidentBlue },
              };
            })
            .reverse(),
        },
      ],
    };
    this.securitiesBarChart.setOption(this.securitiesBarChartOptions);
  };
  getDefaultChartOptions = (titleName, chartData) => {
    let that = this;
    let pieChartOptions = {
      title: {
        text: titleName,
        textStyle: {
          fontFamily: "Seravek",
        },
      },

      tooltip: {
        trigger: "item",
        // "{b} : {d}%"
        formatter: (params) => {
          return ` ${params.data.name} -  ${params.data.value.toLocaleString(
            "en-EN",
            { style: "currency", currency: that.displayCurrency }
          )}
            - ${that.getPercentageOfAssetClass(params.data.value)} %`;
        },
      },
      grid: {
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: {
            title: "Save as Image",
          },
        },
      },
      series: {
        type: "pie",
        radius: ["50%", "70%"],
        center: ["50%", "60%"],
        avoidLabelOverlap: true,
        labelLine: {
          normal: {
            show: window.screen.width > this.minScreenWidth,
          },
        },
        label: {
          normal: {
            show: window.screen.width > this.minScreenWidth,
            fontSize: window.screen.width > 1920 ? 24 : 12,
          },
        },
        data: chartData,
      },
    };

    return pieChartOptions;
  };

  exportToCSV = () => {
    exportToCSV(
      this.securityBalancesGridOptions.grid.filtered,
      "Security Balances",
      true
    );
  };

  normaliseNumbers = (labelValue) => {
    // Nine Zeroes for Billions
    return Math.abs(Number(labelValue)) >= 1.0e9
      ? Math.abs(Number(labelValue)) / 1.0e9 + "B"
      : // Six Zeroes for Millions
      Math.abs(Number(labelValue)) >= 1.0e6
      ? Math.abs(Number(labelValue)) / 1.0e6 + "M"
      : // Three Zeroes for Thousands
      Math.abs(Number(labelValue)) >= 1.0e3
      ? Math.abs(Number(labelValue)) / 1.0e3 + "K"
      : Math.abs(Number(labelValue));
  };
}

export const securityBalances: angular.IComponentOptions = {
  template: require("./securityBalances.component.html"),
  controller: SecurityBalancesComponent,
  bindings: {
    securityBalances: "<",
    assetClassName: "<",
    totalAssetClassMarketValue: "<",
    totalMarketValue: "<",
    displayCurrency: "<",
  },
};



// WEBPACK FOOTER //
// ./src/app/portfoliomanagement/securityBalances/securityBalances.component.ts